/**
 * @fileoverview PayPal Invoice API Hooks
 *
 * ⚠️ INVOICE NUMBER GENERATION: Use generateInvoiceNumber() ⚠️
 * ============================================================
 * STANDARDIZED FORMAT: INV-XXX-XXX (e.g., INV-234-567)
 * SOURCE OF TRUTH: /src/utils/invoiceNumber.ts
 *
 * When creating invoices, ensure invoice numbers use the standard format:
 * import { generateInvoiceNumber } from '@/utils/invoiceNumber';
 * const invoiceNumber = generateInvoiceNumber(); // "INV-234-567"
 *
 * ARCHITECTURE DECISION: PayPal as Source of Truth
 * ================================================
 * These hooks interact DIRECTLY with PayPal via our backend proxy.
 * We DO NOT save invoices to our local database.
 *
 * All invoice data is stored and managed in PayPal:
 * - Invoice creation → PayPal
 * - Invoice updates → PayPal
 * - Invoice status → PayPal
 * - Payment processing → PayPal
 *
 * Backend acts as a proxy layer that:
 * 1. Adds authentication (OAuth 2.0)
 * 2. Transforms data formats
 * 3. Provides caching where appropriate
 *
 * @module usePayPalInvoices
 * @requires @tanstack/react-query
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../../services/api.service';
import { logger } from '../../../../utils/logger';
import type { InvoiceFilters, PaginationParams } from '../types';
import type { InvoiceListItem } from '../../../../types/invoice.types';

// ===================================================================
// PAYPAL API ENDPOINT CONFIGURATION
// ===================================================================

const PAYPAL_ENDPOINT = '/api/v1/paypal/invoices';
const PAYPAL_SIMPLE_ENDPOINT = '/api/v1/paypal/invoices/simple';

/**
 * Query key factory for PayPal invoice queries
 */
export const paypalInvoiceKeys = {
  all: ['paypal-invoices'] as const,
  lists: () => [...paypalInvoiceKeys.all, 'list'] as const,
  list: (filters?: InvoiceFilters, pagination?: PaginationParams) =>
    [...paypalInvoiceKeys.lists(), { filters, pagination }] as const,
  details: () => [...paypalInvoiceKeys.all, 'detail'] as const,
  detail: (id: string) => [...paypalInvoiceKeys.details(), id] as const,
};

/**
 * @interface PayPalInvoiceListResponse
 * @description Paginated response structure from PayPal API
 */
interface PayPalInvoiceListResponse {
  data: InvoiceListItem[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

/**
 * @function usePayPalInvoiceList
 * @description Query hook for fetching invoices from PayPal
 *
 * Data Source: PayPal Invoicing API v2
 * Cache Strategy: 30-second stale time (PayPal data changes less frequently)
 */
export function usePayPalInvoiceList(
  filters?: InvoiceFilters,
  pagination?: PaginationParams
) {
  return useQuery({
    queryKey: paypalInvoiceKeys.list(filters, pagination),
    queryFn: async (): Promise<PayPalInvoiceListResponse> => {
      const params: Record<string, any> = {};

      // Add pagination params
      if (pagination?.page) params.page = pagination.page;
      if (pagination?.pageSize) params.pageSize = pagination.pageSize;

      // Add filter params
      if (filters?.status) params.status = filters.status;
      if (filters?.search) params.search = filters.search;

      logger.info('[usePayPalInvoiceList] Fetching invoices from PayPal', { filters, pagination });

      const response = await apiClient.get<PayPalInvoiceListResponse>(PAYPAL_ENDPOINT, { params });

      logger.info('[usePayPalInvoiceList] Invoices fetched from PayPal', { total: response.data.total });

      return response.data;
    },
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });
}

/**
 * @function usePayPalInvoice
 * @description Query hook for fetching a single invoice from PayPal
 */
export function usePayPalInvoice(id: string) {
  return useQuery({
    queryKey: paypalInvoiceKeys.detail(id),
    queryFn: async () => {
      logger.info('[usePayPalInvoice] Fetching invoice from PayPal', { invoiceId: id });

      const response = await apiClient.get(`${PAYPAL_ENDPOINT}/${id}`);

      logger.info('[usePayPalInvoice] Invoice fetched from PayPal', { invoiceId: id });

      return response.data;
    },
    enabled: !!id,
    staleTime: 60000, // Consider data fresh for 1 minute
  });
}

/**
 * @function useCreatePayPalInvoice
 * @description Mutation hook for creating invoices directly in PayPal
 *
 * Uses the simplified endpoint that accepts frontend format and transforms to PayPal format.
 */
export function useCreatePayPalInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceData: any) => {
      logger.info('[useCreatePayPalInvoice] Creating invoice in PayPal', { invoiceData });

      // Use the simplified endpoint that handles transformation
      const response = await apiClient.post(PAYPAL_SIMPLE_ENDPOINT, invoiceData);

      logger.info('[useCreatePayPalInvoice] Invoice created in PayPal', { invoiceId: response.data.id });

      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate PayPal invoice lists
      queryClient.invalidateQueries({ queryKey: paypalInvoiceKeys.lists() });

      // Optimistically add to cache
      queryClient.setQueryData(paypalInvoiceKeys.detail(data.id), data);

      logger.info('[useCreatePayPalInvoice] Cache updated for new PayPal invoice');
    },
    onError: (error) => {
      logger.error('[useCreatePayPalInvoice] Mutation error', { error });
    },
  });
}

/**
 * @function useSendPayPalInvoice
 * @description Mutation hook for sending invoices via PayPal
 */
export function useSendPayPalInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      logger.info('[useSendPayPalInvoice] Sending invoice via PayPal', { invoiceId });

      const response = await apiClient.post(`${PAYPAL_ENDPOINT}/${invoiceId}/send`);

      logger.info('[useSendPayPalInvoice] Invoice sent via PayPal', { invoiceId });

      return response.data;
    },
    onSuccess: (_, invoiceId) => {
      // Invalidate lists and detail to refresh status
      queryClient.invalidateQueries({ queryKey: paypalInvoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paypalInvoiceKeys.detail(invoiceId) });

      logger.info('[useSendPayPalInvoice] Cache updated after sending');
    },
    onError: (error) => {
      logger.error('[useSendPayPalInvoice] Mutation error', { error });
    },
  });
}

/**
 * @function useCancelPayPalInvoice
 * @description Mutation hook for cancelling invoices in PayPal
 *
 * Cancels a sent invoice in PayPal. The invoice status will be updated to CANCELLED.
 * Note: Only sent invoices can be cancelled. Draft invoices should be deleted instead.
 */
export function useCancelPayPalInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      logger.info('[useCancelPayPalInvoice] Cancelling invoice in PayPal', { invoiceId });

      const response = await apiClient.post(`${PAYPAL_ENDPOINT}/${invoiceId}/cancel`);

      logger.info('[useCancelPayPalInvoice] Invoice cancelled in PayPal', { invoiceId });

      return response.data;
    },
    onSuccess: (_, invoiceId) => {
      // Invalidate lists and detail to refresh status
      queryClient.invalidateQueries({ queryKey: paypalInvoiceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: paypalInvoiceKeys.detail(invoiceId) });

      logger.info('[useCancelPayPalInvoice] Cache updated after cancelling');
    },
    onError: (error) => {
      logger.error('[useCancelPayPalInvoice] Mutation error', { error });
    },
  });
}

/**
 * @function useDeletePayPalInvoice
 * @description Mutation hook for deleting invoices from PayPal
 */
export function useDeletePayPalInvoice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (invoiceId: string) => {
      logger.info('[useDeletePayPalInvoice] Deleting invoice from PayPal', { invoiceId });

      await apiClient.delete(`${PAYPAL_ENDPOINT}/${invoiceId}`);

      logger.info('[useDeletePayPalInvoice] Invoice deleted from PayPal', { invoiceId });
    },
    onMutate: async (invoiceId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: paypalInvoiceKeys.lists() });

      // Snapshot previous lists
      const previousLists = queryClient.getQueriesData({ queryKey: paypalInvoiceKeys.lists() });

      // Optimistically remove from all lists
      queryClient.setQueriesData(
        { queryKey: paypalInvoiceKeys.lists() },
        (old: PayPalInvoiceListResponse | undefined) => {
          if (!old) return old;
          return {
            ...old,
            data: old.data.filter(invoice => invoice.id !== invoiceId),
            total: old.total - 1,
          };
        }
      );

      return { previousLists };
    },
    onError: (error, invoiceId, context) => {
      // Rollback on error
      if (context?.previousLists) {
        context.previousLists.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
      logger.error('[useDeletePayPalInvoice] Mutation error, rolled back', { error, invoiceId });
    },
    onSuccess: (_, invoiceId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: paypalInvoiceKeys.detail(invoiceId) });

      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: paypalInvoiceKeys.lists() });

      logger.info('[useDeletePayPalInvoice] Cache cleaned up');
    },
  });
}
